import type { EmployeeRow } from "@/hooks/use-employees";
import { cn } from "@/lib/utils";
import { SortableHeader } from "@/components/shared/SortableHeader";
import { ClickableRow } from "@/components/shared/ClickableRow";
import { SYSTEM_ROLE_LABELS, ROLE_COLORS } from "@/lib/constants";

export type SortKey = "name" | "role" | "capacity" | "projects" | "hours";
export type SortDir = "asc" | "desc";

export function sortEmployees(list: EmployeeRow[], key: SortKey, dir: SortDir): EmployeeRow[] {
  const sorted = [...list].sort((a, b) => {
    switch (key) {
      case "name": return a.full_name.localeCompare(b.full_name, "nb");
      case "role": return (a.role ?? "").localeCompare(b.role ?? "", "nb");
      case "capacity": return (a.monthly_capacity_hours ?? 0) - (b.monthly_capacity_hours ?? 0);
      case "projects": return a.project_count - b.project_count;
      case "hours": return a.total_budgeted_hours - b.total_budgeted_hours;
    }
  });
  return dir === "desc" ? sorted.reverse() : sorted;
}

export function EmployeeTable({
  sorted,
  sortKey,
  sortDir,
  toggleSort,
  onRowClick,
}: {
  sorted: EmployeeRow[];
  sortKey: SortKey;
  sortDir: SortDir;
  toggleSort: (key: SortKey) => void;
  onRowClick: (user: EmployeeRow) => void;
}) {
  const thClass = "py-3 px-2 text-xs uppercase tracking-wider";
  const header = (key: SortKey, label: string, align: "left" | "right") => (
    <SortableHeader
      label={label}
      active={sortKey === key}
      dir={sortDir}
      onToggle={() => toggleSort(key)}
      align={align}
      className={thClass}
    />
  );

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b">
          {header("name", "Navn", "left")}
          {header("role", "Rolle", "left")}
          {header("capacity", "Kapasitet", "right")}
          {header("projects", "Prosjekter", "right")}
          {header("hours", "Tildelte timer", "right")}
        </tr>
      </thead>
      <tbody>
        {sorted.length === 0 ? (
          <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Ingen ansatte funnet.</td></tr>
        ) : sorted.map((u) => {
          const initials = u.full_name.split(" ").map((n) => n[0]).join("").slice(0, 2);
          const roleColor = ROLE_COLORS[u.role] ?? "bg-gray-100 text-gray-600";
          const roleLabel = SYSTEM_ROLE_LABELS[u.role] ?? u.role;

          return (
            <ClickableRow
              key={u.id}
              data-testid="employee-row"
              data-employee-id={u.id}
              variant="link"
              className="border-b border-border/40 hover:bg-muted/30 transition-colors"
              onActivate={() => onRowClick(u)}
            >
              <td className="py-3 px-2">
                <div className="flex items-center gap-2.5">
                  {u.avatar_url ? (
                    <img src={u.avatar_url} alt={u.full_name} className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <span className="w-10 h-10 rounded-full bg-primary/10 text-primary text-sm font-medium flex items-center justify-center">{initials}</span>
                  )}
                  <div>
                    <span className="font-medium">{u.full_name}</span>
                    <p className="text-xs text-muted-foreground">{u.email}</p>
                  </div>
                </div>
              </td>
              <td className="py-3 px-2">
                <span className={cn("text-xs px-2 py-0.5 rounded font-medium", roleColor)}>{roleLabel}</span>
              </td>
              <td className="py-3 px-2 text-right tabular-nums">{u.monthly_capacity_hours != null ? `${u.monthly_capacity_hours}t/mnd` : "—"}</td>
              <td className="py-3 px-2 text-right tabular-nums">{u.project_count}</td>
              <td className="py-3 px-2 text-right tabular-nums">{u.total_budgeted_hours > 0 ? `${u.total_budgeted_hours}t` : "—"}</td>
            </ClickableRow>
          );
        })}
      </tbody>
    </table>
  );
}
